import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { ITeamMission } from 'src/app/storage/interfaces/team-mission.interface';
import { ITeam } from 'src/app/storage/interfaces/team.interface';
import { TeamMission } from 'src/app/storage/models/team-mission.model';
import { Team } from 'src/app/storage/models/team.model';
import { PeopleLocation } from 'src/app/storage/models/people-location.model';
import { environment } from 'src/environments/environment';
import { ApiService } from './api.service';
import { IPeopleLocation } from 'src/app/storage/interfaces/poeple-location.interface';
import { MobileDevice } from 'src/app/storage/models/mobile-device.model';
import { IMobileDevice } from 'src/app/storage/interfaces/mobile-device.interface';

const URL = environment.aws.amplify.API.REST + 'teams';

@Injectable({
  providedIn: 'root'
})
export class TeamsService {

  missionTeams$: BehaviorSubject<TeamMission[]> = new BehaviorSubject(null);
  missionSupportTeams$: BehaviorSubject<TeamMission[]> = new BehaviorSubject(null);

  constructor(
    private translateService: TranslateService,
    private apiService: ApiService
  ) { }

  async getTeamsWithRole(params: IGetTeamsWithRoleParams): Promise<Team[] | null> {
    const translations = await this.translateService.get('TEAMS').toPromise();

    const response = await this.apiService.get(
      `${URL}/market/${params.marketname}/role/${params.rolename}`,
      translations['LABEL'],
      { showLoader: true }
    );

    return response ? response.teams.map((team: ITeam) => new Team(team)) : null;
  }

  async getTeamsWithGroup(params: IGetTeamsWithGroupParams): Promise<Team[] | null> {
    const translations = await this.translateService.get('TEAMS').toPromise();

    const response = await this.apiService.get(
      `${URL}/market/${params.marketname}/group/${params.groupname}`,
      translations['LABEL'],
      { showLoader: true }
    );

    return response ? response.teams.map((team: ITeam) => new Team(team)) : null;
  }

  /* ################ MISSION TEAMS ################ */

  async getMissionTeams(params: IGetMissionTeamWithRoleParams): Promise<TeamMission[] | null> {
    const translations = await this.translateService.get('TEAMS').toPromise();

    const response = await this.apiService.get(
      `${URL}/mission/${params.missionId}/role/${params.rolename}`,
      translations['LABEL']
    );

    return response ? response.missionTeams.map((missionTeam: ITeamMission) => new TeamMission(missionTeam)) : null;
  }

  async addUpdateMissionTeam(params: IAddUpdateMissionTeamParams): Promise<boolean> {
    const translations = await this.translateService.get('TEAMS').toPromise();
    const missionTeam = params.missionTeam;

    const payload = {
      teamMemberId: missionTeam.teamMemberId,
      teammissionleader: missionTeam.teammissionleader,
      teammissionapprentice: missionTeam.teammissionapprentice,
      mobileDevicepk: missionTeam.mobileDevicepk,
      mobileDeviceLTEProvider: missionTeam.mobileDeviceLTEProvider,
      mobileDeviceSimNumber: missionTeam.mobileDeviceSimNumber
    };

    const response = await this.apiService.post(
      `${URL}/mission/${params.missionId}/role/${params.rolename}`,
      payload,
      translations['LABEL'],
      { showLoader: true }
    );

    return response != null;
  }

  async removeMissionTeam(params: IRemoveMissionTeamParams): Promise<boolean> {
    const translations = await this.translateService.get('TEAMS').toPromise();

    const response = await this.apiService.delete(
      `${URL}/mission/${params.missionId}/role/${params.rolename}/${params.missionTeam.teamMemberId}`,
      translations['LABEL'],
      { showLoader: true }
    );

    return response != null;
  }

  async setDefaultSupportTeams(missionId: string): Promise<boolean> {
    const translations = await this.translateService.get('TEAMS').toPromise();

    const response = await this.apiService.post(
      `${URL}/mission/${missionId}/support`,
      null,
      translations['LABEL']
    );

    return response != null;
  }

  /* ################ TEAM MANAGEMENT ################ */

  async getMarketTeams(marketname: string): Promise<Team[] | null> {
    const translations = await this.translateService.get('TEAMS').toPromise();

    const response = await this.apiService.get(
      `${URL}/market/${marketname}`,
      translations['LABEL'],
      { showLoader: true }
    );

    return response ?
          response.teams.map((team: ITeam) => new Team(team)) :
          null ;
  }

  async addTeam(marketname: string, team: Team): Promise<boolean> {
    const translations = await this.translateService.get('TEAMS').toPromise();

    const response = await this.apiService.put(
      `${URL}/market/${marketname}`,
      team,
      translations['LABEL'],
      { showLoader: true }
    );

    return response != null;
  }

  async updateTeam(marketname: string, team: Team): Promise<boolean> {
    const translations = await this.translateService.get('TEAMS').toPromise();

    const response = await this.apiService.post(
      `${URL}/market/${marketname}`,
      team,
      translations['LABEL'],
      { showLoader: true }
    );

    return response != null;
  }

  async removeTeam(marketname: string, team: Team): Promise<boolean> {
    const translations = await this.translateService.get('TEAMS').toPromise();

    const response = await this.apiService.delete(
      `${URL}/market/${marketname}/${team.teamMemberId}`,
      translations['LABEL'],
      { showLoader: true }
    );

    return response != null;
  }

  async addTeamRole(teamMemberId: string, rolename: string): Promise<boolean> {
    const translations = await this.translateService.get('TEAMS').toPromise();

    const response = await this.apiService.put(
      `${URL}/role/${rolename}/${teamMemberId}`,
      { },
      translations['LABEL'],
      { showLoader: true }
    );

    return response != null;
  }

  async removeTeamRole(teamMemberId: string, rolename: string): Promise<boolean> {
    const translations = await this.translateService.get('TEAMS').toPromise();

    const response = await this.apiService.delete(
      `${URL}/role/${rolename}/${teamMemberId}`,
      translations['LABEL'],
      { showLoader: true }
    );

    return response != null;
  }

  /* ################ PEOPLE LOCATIONS ################ */

  async getMarketPeopleLocations(marketname: string): Promise<PeopleLocation[] | null> {
    const translations = await this.translateService.get('TEAMS').toPromise();

    const response = await this.apiService.get(
      `${URL}/people-location/market/${marketname}`,
      translations['LABEL'],
      { showLoader: true }
    );

    return response ?
          response.peopleLocations.map((peopleLocation: IPeopleLocation) => new PeopleLocation(peopleLocation)) :
          null ;
  }

  /* ################ MOBILE DEVICES ################ */

  async getMobileDevices(): Promise<MobileDevice[] | null> {
    const translations = await this.translateService.get('TEAMS').toPromise();

    const response = await this.apiService.get(
      `${URL}/mobile-device`,
      translations['LABEL'],
      { showLoader: true }
    );

    return response ?
          response.mobileDevices.map((mobileDevice: IMobileDevice) => new MobileDevice(mobileDevice)) :
          null ;
  }
}

interface IGetTeamsWithRoleParams {
  marketname: string;
  rolename: string;
}

interface IGetTeamsWithGroupParams {
  marketname: string;
  groupname: string;
}

interface IGetMissionTeamWithRoleParams {
  missionId: string;
  rolename: string;
}

interface IAddUpdateMissionTeamParams {
  missionId: string;
  rolename: string;
  missionTeam: TeamMission;
}

interface IRemoveMissionTeamParams {
  missionId: string;
  rolename: string;
  missionTeam: TeamMission;
}